import type { ReactNode } from 'react'
import s from './BulkBar.module.css'

export interface BulkBarProps {
  /** Combien de lignes sont cochées. À zéro, la barre ne se monte pas — voir ci-dessous. */
  count: number
  /** Le nom de ce qu'on a coché, au singulier : « demande », « contrôle ». */
  unit: string
  /**
   * Le participe qui suit `unit`, **accordé par l'écran** : « sélectionné(s) » pour « contrôle ».
   * Le genre d'un nom est du texte métier, que ce paquet ne peut pas deviner ; sans cette prop, le
   * féminin de « demande », premier nom compté ici.
   */
  selectedLabel?: string
  /** Les actions, dans l'ordre du prototype. Rejetées à droite. */
  children: ReactNode
}

/**
 * La bande d'**actions groupées** d'une liste — ce qui apparaît dès qu'une ligne est cochée.
 *
 * Report de `.bulkbar` du prototype back-office : elle se pose **dans la `Card`, avant le
 * `DataTable`**, et ses coins arrondis en haut épousent ceux de la carte.
 *
 * ⚠️ **Elle ne se monte pas à zéro, elle ne se masque pas.** Le prototype la cache par
 * `display: none` parce qu'il rend son écran une fois pour toutes ; ici elle est absente du DOM
 * tant que rien n'est coché — une barre masquée resterait tabulable et annoncée, et les lecteurs
 * d'écran liraient trois boutons sans objet au-dessus de chaque tableau.
 *
 * ⚠️ **Le décompte est un `<output>`** — qui porte déjà `role="status"`, donc une région vivante :
 * cocher une case ne déplace pas le focus, et rien n'annoncerait sans cela que la sélection a
 * changé. C'est la seule façon qu'un utilisateur au clavier sache sur combien de lignes son geste
 * va porter. Le rôle ne se redéclare pas (`jsx-a11y/no-redundant-roles`).
 *
 * ```tsx
 * <Card>
 *   {selected.length > 0 && (
 *     <BulkBar count={selected.length} unit="demande">
 *       <Button size="sm" tone="neutral" onClick={assign}>Attribuer à une équipe</Button>
 *       <Button size="sm" tone="ghost" onClick={clear}>Annuler</Button>
 *     </BulkBar>
 *   )}
 *   <DataTable>…</DataTable>
 * </Card>
 * ```
 */
export function BulkBar({
  count,
  unit,
  selectedLabel = 'sélectionnée(s)',
  children,
}: BulkBarProps) {
  return (
    <div className={s.root}>
      {/* Le pluriel entre parenthèses est celui du prototype — « 3 demande(s) sélectionnée(s) » :
          il vaut pour un comme pour trois, et évite une règle d'accord par nombre. Le genre, lui,
          reste à l'écran (`selectedLabel`). */}
      <output className={s.count}>
        <b>{count}</b> {unit}(s) {selectedLabel}
      </output>
      <div className={s.actions}>{children}</div>
    </div>
  )
}
